import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useStatusBarSurface } from '../hooks/useStatusBarSurface';
import { signOutPortal } from '../lib/portalSignOut';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * PD-16721 — where the storefront lands after a user deletes their own account
 * from the Account page, and the final step of the flow the App Store asks to
 * see. Chrome-less and outside PortalLayout, like the password pages.
 *
 * The Account page navigates here with `state.signOut` and this page ends the
 * local session once it has mounted: outside PortalLayout, the sign-out cannot
 * trip the layout's "signed out, go to /" redirect. The server already ended
 * every session when the account was deleted.
 *
 * Follows the portal's pre-login `loginTheme`, matching ForgotPasswordPage.
 */
export function AccountDeletedPage(): JSX.Element {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const shouldSignOut =
		(location.state as { signOut?: boolean } | null)?.signOut === true;
	const loginTheme = usePortalSiteStore((s) => s.site?.loginTheme ?? 'dark');
	const supportContact = usePortalSiteStore(
		(s) => s.site?.supportContact ?? null,
	);
	const isLight = loginTheme === 'light';
	// No header band: the status bar reads against this page (PD-16744).
	useStatusBarSurface('page');

	useEffect(() => {
		if (!shouldSignOut) {
			return;
		}
		// Drop the flag first so a refresh or Back does not repeat it.
		void navigate('/account-deleted', { replace: true, state: null });
		void signOutPortal({ revoke: true });
	}, [navigate, shouldSignOut]);

	const pageCls = isLight ? 'bg-surface' : 'bg-brand-page-bg';
	const cardCls = isLight
		? 'bg-surface border-default shadow-xl'
		: 'bg-brand-surface border-brand-border';
	const bodyCls = isLight ? 'text-muted' : 'text-brand-muted';

	const supportEmail = supportContact?.email ?? null;
	const supportPhone = supportContact?.phone ?? null;
	const hasSupport =
		(supportEmail != null && supportEmail !== '') ||
		(supportPhone != null && supportPhone !== '');

	return (
		<div
			className={`min-h-dvh flex items-center justify-center px-4 py-8 ${pageCls}`}
		>
			<div
				className={`w-full max-w-md border rounded-xl p-6 space-y-4 ${cardCls}`}
			>
				<FontAwesomeIcon
					icon={faCircleCheck}
					className="text-3xl text-portal-accent"
				/>
				<h1 className="text-2xl font-semibold text-default">
					{t('Your account has been deleted')}
				</h1>
				<p className={`text-sm ${bodyCls}`}>
					{t(
						'Your login and your name, email and phone number have been removed, and you have been signed out.',
					)}
				</p>
				<p className={`text-sm ${bodyCls}`}>
					{t(
						"Your business's customer account is not affected. Its orders, invoices and payments stay with us, and others at your business can keep using their own logins.",
					)}
				</p>
				{hasSupport && (
					<p className={`text-sm ${bodyCls}`}>
						{t('Need access again? Contact us at')}{' '}
						{supportEmail != null && supportEmail !== '' && (
							<a
								href={`mailto:${supportEmail}`}
								className="text-portal-accent hover:underline"
							>
								{t(supportEmail)}
							</a>
						)}
						{supportEmail != null &&
							supportEmail !== '' &&
							supportPhone != null &&
							supportPhone !== '' && <> {t('or')} </>}
						{supportPhone != null && supportPhone !== '' && (
							<a
								href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`}
								className="text-portal-accent hover:underline"
							>
								{t(supportPhone)}
							</a>
						)}
						{t('.')}
					</p>
				)}
				<Link
					to="/"
					replace
					className="inline-block text-sm font-medium text-portal-accent hover:underline"
				>
					{t('Back to home')}
				</Link>
			</div>
		</div>
	);
}
